import { useCallback, useEffect, useRef, useState } from 'react'
import type { Image } from '@publiqa/shared'

/**
 * Galerie d'article : mosaïque de photos ; un clic ouvre la visionneuse
 * (flèches, clavier ← → Échap, balayage au doigt).
 */
export function Gallery({ images, caption }: { images: Image[]; caption: string }) {
  const [open, setOpen] = useState<number | null>(null)
  if (!images.length) return null
  return (
    <figure className={`gallery gallery-${Math.min(images.length, 4)}`}>
      <div className="gallery-grid">
        {images.map((img, i) => (
          <button
            type="button"
            key={i}
            className="gallery-item"
            onClick={() => setOpen(i)}
            aria-label={`Agrandir la photo ${i + 1} sur ${images.length}${img.alt ? ` : ${img.alt}` : ''}`}
          >
            <img src={img.url} alt={img.alt} loading="lazy" />
          </button>
        ))}
      </div>
      {caption && <figcaption>{caption}</figcaption>}
      {open !== null && <Lightbox images={images} start={open} onClose={() => setOpen(null)} />}
    </figure>
  )
}

function Lightbox({
  images,
  start,
  onClose,
}: {
  images: Image[]
  start: number
  onClose: () => void
}) {
  const [i, setI] = useState(start)
  const ref = useRef<HTMLDialogElement>(null)
  const touchX = useRef<number | null>(null)
  const go = useCallback(
    (d: number) => setI((x) => (x + d + images.length) % images.length),
    [images.length],
  )

  useEffect(() => {
    const d = ref.current
    d?.showModal()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1)
      if (e.key === 'ArrowRight') go(1)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      d?.close()
    }
  }, [go])

  const img = images[i]!
  return (
    <dialog
      ref={ref}
      className="lightbox"
      onCancel={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      onTouchStart={(e) => (touchX.current = e.touches[0]?.clientX ?? null)}
      onTouchEnd={(e) => {
        const s = touchX.current
        const end = e.changedTouches[0]?.clientX
        if (s !== null && end !== undefined && Math.abs(end - s) > 40) go(end < s ? 1 : -1)
        touchX.current = null
      }}
      aria-label="Visionneuse de photos"
    >
      <img src={img.url} alt={img.alt} />
      <p className="lightbox-caption">
        {img.alt && <span>{img.alt}</span>}
        <span className="lightbox-count">
          {i + 1} / {images.length}
        </span>
      </p>
      {images.length > 1 && (
        <>
          <button
            type="button"
            className="lightbox-nav prev"
            onClick={() => go(-1)}
            aria-label="Photo précédente"
          >
            ‹
          </button>
          <button
            type="button"
            className="lightbox-nav next"
            onClick={() => go(1)}
            aria-label="Photo suivante"
          >
            ›
          </button>
        </>
      )}
      <button type="button" className="lightbox-close" onClick={onClose} aria-label="Fermer">
        ✕
      </button>
    </dialog>
  )
}
